/**
 * CV Data - ATS Optimized for 2026
 *
 * This data structure is designed to be parsed correctly by:
 * - ATS (Applicant Tracking Systems) used by 97.8% of Fortune 500
 * - AI-powered resume screening tools
 * - Semantic analysis systems
 *
 * Key optimizations:
 * - Keywords include both acronyms and full terms
 * - Achievements are quantified with metrics
 * - Skills grouped by category for better parsing
 * - Single-column layout friendly data structure
 */

export interface CVData {
  personal: {
    name: string;
    title: string;
    email: string;
    phone?: string;
    linkedin: string;
    website: string;
    location: string;
    languages: { language: string; level: string }[];
  };
  summary: string;
  skills: {
    category: string;
    items: string[];
  }[];
  experience: {
    role: string;
    company: string;
    location: string;
    startDate: string;
    endDate: string;
    achievements: string[];
  }[];
  education: {
    degree: string;
    institution: string;
    year: string;
    description?: string;
  }[];
  keyProjects: {
    name: string;
    description: string;
    impact: string;
    technologies: string[];
  }[];
}

export const cvData: CVData = {
  personal: {
    name: 'Fernando Olea',
    title: 'CTO & Tech Lead | XR Developer | Full-Stack AI Engineer',
    email: 'contacto@fernandoolea.com',
    phone: '+34 636 27 60 78',
    linkedin: 'linkedin.com/in/fernando-olea-lyceum',
    website: 'fernandoolea.com',
    location: 'Madrid, Spain | Open to Remote & Relocation',
    languages: [
      { language: 'Spanish', level: 'Native' },
      { language: 'English', level: 'Professional' },
    ],
  },

  summary: `**CTO and Tech Lead** with **9+ years** of experience leading cross-functional teams (up to **15 people**) and delivering innovative technology solutions across **XR (AR/VR/MR)**, **full-stack web development**, and **AI integration**. Founded and scaled **Lyceum VR**, driving international projects for clients including **Verizon**, **Arcadia Earth** (NY, Las Vegas, Toronto), and high-profile events like **New York Fashion Week**. Expert in **Unity/C#** (9+ years hands-on experience, enhanced with AI tools) and **AI-Powered full-stack development**. Proven track record in technical strategy, team leadership, and transforming complex requirements into scalable products. Passionate about **R&D** and implementing cutting-edge solutions.`,

  skills: [
    {
      category: 'Leadership & Management',
      items: [
        'Technical Team Leadership (up to 15 people)',
        'Cross-functional Team Management',
        'Technical Strategy & Roadmap',
        'Agile Methodologies (Scrum, Kanban)',
        'Project Management',
        'Stakeholder Communication',
        'Budget Management',
        'Hiring & Talent Development',
        'OKRs & KPIs Definition',
        'Vendor Management',
        'Risk Assessment & Mitigation',
      ],
    },
    {
      category: 'Soft Skills',
      items: [
        'Strategic Thinking',
        'Decision Making Under Pressure',
        'Problem Solving',
        'Executive Communication',
        'Negotiation',
        'Conflict Resolution',
        'Mentoring & Coaching',
        'Adaptability',
        'Innovation Mindset',
        'Results-Oriented',
        'Cross-cultural Communication',
        'Entrepreneurial Thinking',
      ],
    },
    {
      category: 'XR Development - Unity/C# (9+ years traditional + AI-enhanced)',
      items: [
        'Unity 3D',
        'C# Expert',
        'Mixed Reality Toolkit (MRTK)',
        'XR Interaction Toolkit',
        'ARFoundation',
        'ARKit',
        'ARCore',
        'Vuforia',
        'OpenXR',
        'WebXR',
        'Digital Twins',
        'Microsoft HoloLens 2',
        'Meta Quest (2, 3, Pro)',
        'Spatial Mapping',
        'Hand Tracking',
        'Location-Based Tracking',
        'Custom SLAM Systems',
      ],
    },
    {
      category: 'Programming Languages',
      items: [
        'C# (Expert - 9+ years traditional experience)',
        'TypeScript (Advanced - AI-Powered development)',
        'JavaScript (Advanced)',
        'Python (Intermediate)',
        'C++ (Intermediate)',
        'Java',
        'PHP',
        'HLSL/GLSL Shaders',
      ],
    },
    {
      category: 'Full-Stack Web Development (AI-Powered)',
      items: [
        'React',
        'Next.js',
        'TypeScript',
        'JavaScript',
        'Node.js',
        'Supabase',
        'PostgreSQL',
        'REST APIs',
        'WebGL',
        'Unity WebGL',
        'HTML5',
        'CSS3',
        'Docker',
        'AI-Assisted Development (Claude Code, Cursor)',
      ],
    },
    {
      category: 'AI & Machine Learning Integration',
      items: [
        'OpenAI API Integration',
        'Azure AI Services',
        'Computer Vision',
        'Natural Language Processing (NLP)',
        'AI Agents Development',
        'LangChain/LangGraph',
        'RAG (Retrieval Augmented Generation)',
        'Embeddings',
        'Vector Databases',
        'Fine-tuning',
        'Claude Code',
        'Cursor AI',
        'Prompt Engineering',
        'AI-Assisted Development',
      ],
    },
    {
      category: 'Cloud & DevOps',
      items: [
        'Amazon Web Services (AWS)',
        'Microsoft Azure',
        'Google Cloud Platform (GCP)',
        'Netlify',
        'CI/CD Pipelines',
        'Git',
        'Plastic SCM',
        'Docker',
      ],
    },
    {
      category: '3D & Visualization',
      items: [
        '3ds Max',
        'V-Ray Rendering',
        'Blender',
        'Photogrammetry',
        '3D Modeling',
        'Real-time Rendering',
        'GPU Optimization',
        'Performance Optimization',
      ],
    },
    {
      category: 'Networking & Multiplayer',
      items: [
        'Photon Networking',
        'Real-time Multiplayer Systems',
        'Client-Server Architecture',
        'Voice Communication Integration',
        'Multi-device Synchronization',
      ],
    },
  ],

  experience: [
    {
      role: 'CTO & Tech Lead',
      company: 'HausLook',
      location: 'Madrid, Spain',
      startDate: 'May 2025',
      endDate: 'Present',
      achievements: [
        'Leading **technical strategy** for an integrated platform connecting bathroom, kitchen, and exterior design providers with end customers',
        'Architected **full-stack solution** using **React, TypeScript, and Supabase**, deployed on Netlify',
        'Developed interactive **3D configurator** in **Unity WebGL** for real-time product customization',
        'Implemented **AI agents** for automated data management, reducing manual processing time by **60%**',
        'Established **photorealistic rendering pipeline** with **3ds Max and V-Ray** for client presentations',
        'Integrated **AI-assisted development tools** (Claude Code, Cursor) increasing team productivity by **40%**',
      ],
    },
    {
      role: 'Founder, CEO & CTO',
      company: 'Lyceum VR',
      location: 'Madrid, Spain',
      startDate: '2020',
      endDate: 'Present',
      achievements: [
        'Founded and scaled **XR education company**, leading teams of up to **15 developers** and artists',
        'Delivered **20+ international projects** for clients including **Verizon**, **Arcadia Earth**, and Scatola LLC',
        'Developed proprietary **VR education platform** with **10+ interactive experiences** for Meta Quest and PC VR',
        'Created **Popau AR Trading Cards** product, achieving **24 augmented reality cards** with custom AR system using **OpenCV** for non-ARCore devices',
        'Designed and implemented custom **Location-Based Tracking system** without third-party dependencies, controlling **40+ HoloLens 2** devices simultaneously at **NY Fashion Week**',
        'Built **multiplayer XR environments** with **Photon** integration, **AI characters** using OpenAI API, and Azure voice services',
      ],
    },
    {
      role: 'Researcher and Developer (Simulation Software)',
      company: 'CT Ingenieros',
      location: 'Madrid, Spain',
      startDate: '2019',
      endDate: '2020',
      achievements: [
        'Led **R&D** for civil and military **simulation software** development',
        'Supervised university thesis projects (undergraduate and master level)',
        'Developed **simulation systems** using **Unity3D** and proprietary military software',
        'Implemented **real-time training scenarios** for defense applications',
      ],
    },
    {
      role: 'CTO',
      company: '6DLab',
      location: 'Madrid, Spain',
      startDate: '2019',
      endDate: '2019',
      achievements: [
        'Technical Director managing **AR/VR development team**',
        'Established development workflows and **technical standards**',
        'Delivered multiple **AR/VR projects** for enterprise clients',
        'Implemented **Agile methodologies** improving delivery times by **30%**',
      ],
    },
    {
      role: 'VR Program Developer & AR/VR Researcher',
      company: 'LudusVR',
      location: 'Madrid, Spain',
      startDate: '2017',
      endDate: '2019',
      achievements: [
        'Developed **VR applications** for **Industry 4.0** training scenarios',
        'Pioneered **AR/VR research** initiatives for emerging XR technologies',
        'Created **interactive training simulations** reducing onboarding time by **40%**',
        'Collaborated with industrial clients on **digital twin** implementations',
      ],
    },
  ],

  education: [
    {
      degree: 'Master in AI for Developers (AI4Devs)',
      institution: 'LIDR',
      year: '2024 - 2025',
      description:
        'Specialization in AI integration, prompt engineering, and AI-assisted software development',
    },
    {
      degree: 'Bachelor in Computer Engineering',
      institution: 'Universidad de Alcalá de Henares',
      year: '2013 - 2017',
    },
    {
      degree: 'Videogames Development with Unity3D Course',
      institution: 'Universidad de Alcalá de Henares',
      year: '2015',
    },
    {
      degree: 'Android Apps Development Course',
      institution: 'Universidad de Alcalá de Henares',
      year: '2012',
    },
  ],

  keyProjects: [
    {
      name: 'Arcadia Earth HoloLens NY',
      description:
        'Custom location-based tracking system for 1,400 sqm exhibition (2 floors, 15 rooms)',
      impact:
        'Device localization in under 2 seconds anywhere in the museum without individual calibration',
      technologies: ['Unity', 'HoloLens 2', 'MRTK', 'Custom SLAM', 'Spatial Mapping'],
    },
    {
      name: 'Coterie NY Fashion Week 2023/2024',
      description:
        'Custom location-based tracking system for mixed reality fashion experience',
      impact: 'Controlled 40+ HoloLens 2 devices simultaneously with centralized server',
      technologies: ['Unity', 'HoloLens 2', 'Custom SLAM', 'Networking', 'MRTK'],
    },
    {
      name: 'Lyceum Learning VR Platform',
      description:
        'Complete VR education platform with 10+ interactive experiences for primary education',
      impact: 'Deployed in multiple schools, enabling immersive learning with hand tracking',
      technologies: ['Unity', 'Meta Quest', 'Hand Tracking', 'XR Interaction Toolkit'],
    },
    {
      name: 'Multiplayer AI Environment',
      description:
        'Cross-platform multiplayer environment with AI-powered characters',
      impact: 'Seamless communication across Meta Quest, PC, and WebGL platforms',
      technologies: ['Unity', 'Photon', 'OpenAI API', 'Azure Speech Services'],
    },
  ],
};

// Generate keywords for ATS optimization
export const atsKeywords = [
  // Leadership
  'CTO',
  'Chief Technology Officer',
  'Tech Lead',
  'Technical Director',
  'Engineering Manager',
  'Team Lead',
  'Founder',
  'CEO',

  // XR
  'Extended Reality',
  'XR',
  'Virtual Reality',
  'VR',
  'Augmented Reality',
  'AR',
  'Mixed Reality',
  'MR',
  'Spatial Computing',
  'HoloLens',
  'Meta Quest',
  'Oculus',

  // Tech
  'Unity',
  'Unity3D',
  'Unreal Engine',
  'C#',
  'React',
  'TypeScript',
  'JavaScript',
  'Python',
  'Full-Stack',
  'Full Stack',
  'WebGL',

  // AI
  'Artificial Intelligence',
  'AI',
  'Machine Learning',
  'ML',
  'OpenAI',
  'Computer Vision',
  'NLP',
  'AI Agents',

  // Cloud
  'AWS',
  'Amazon Web Services',
  'Azure',
  'Microsoft Azure',
  'Google Cloud',
  'GCP',
  'Cloud Architecture',

  // Methodologies
  'Agile',
  'Scrum',
  'Kanban',
  'CI/CD',
  'DevOps',
];
